import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock3, LockKeyhole, Save, SlidersHorizontal, Zap } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useRole } from "@/context/RoleContext";
import { PageShell } from "@/pages/dashboard/RolePages";

const initial = { enabled: false, energy_cost: 100, duration_hours: 168, revision: 1 };
const fmt = (v) => new Intl.NumberFormat("es-CO").format(Number(v || 0));

export default function NumerologiaDirector() {
  const { role } = useRole();
  const [settings, setSettings] = useState(initial);
  const [overview, setOverview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: config }, { data: metrics }] = await Promise.all([
        api.get("/api/numerology/director/numerology/settings"),
        api.get("/api/numerology/director/numerology/overview"),
      ]);
      setSettings(config);
      setOverview(metrics);
      setError("");
    } catch (e) { setError(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (role === "superadmin") refresh(); }, [role, refresh]);

  const save = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true); setError(""); setNotice("");
    try {
      const payload = {
        enabled: Boolean(settings.enabled),
        energy_cost: Number(settings.energy_cost),
        duration_hours: Number(settings.duration_hours),
        expected_revision: Number(settings.revision),
      };
      const { data } = await api.put("/api/numerology/director/numerology/settings", payload);
      setSettings(data);
      setNotice("Configuración guardada. Los próximos pagos usarán el nuevo precio y duración.");
      await refresh();
    } catch (e) {
      setError(e.response?.data?.detail?.message || formatApiError(e));
      if (e.response?.data?.detail?.code === "SETTINGS_CHANGED") await refresh();
    } finally { setBusy(false); }
  };

  if (role !== "superadmin") return <PageShell title="Gobierno Numerología"><p>Disponible exclusivamente para Dirección KD9.</p></PageShell>;

  return <PageShell title="Gobierno Numerología Viva" desc="Administración de activación, precio, duración y transacciones en Energía KD9." testId="numerology-director-page">
    <div className="max-w-5xl space-y-5">
      {error && <p role="alert" className="p-3 text-red-300 rounded-xl border border-red-400/30">{error}</p>}
      {notice && <p role="status" className="p-3 text-green-300 rounded-xl border border-green-400/30">{notice}</p>}
      {loading && <p className="text-sm text-white/60">Consultando configuración...</p>}
      <form onSubmit={save} className="kd9-card space-y-5 p-5 sm:p-7">
        <h2 className="font-cinzel text-xl kd9-gold-text"><SlidersHorizontal size={20} className="inline mr-2"/>Configuración comercial</h2>
        <label className="flex items-center justify-between gap-4 p-3 rounded-xl border border-amber-400/20">
          <span className="text-sm"><strong>Permitir nuevas compras</strong><span className="block text-xs text-white/60 mt-1">Al desactivar, los accesos pagados vigentes conservan su vencimiento; no se realizan nuevos descuentos.</span></span>
          <input type="checkbox" data-testid="numerology-director-enable" checked={Boolean(settings.enabled)} disabled={loading || busy}
            onChange={e => setSettings(x=>({...x,enabled:e.target.checked}))} className="h-5 w-5 accent-amber-400"/>
        </label>
        <div className="grid sm:grid-cols-2 gap-5">
          <label className="text-sm block">
            <span><Zap size={16} className="inline mr-1"/>Costo por acceso (Energía KD9)</span>
            <input type="number" data-testid="numerology-director-cost" min={1} max={1000000} step={1} required
              value={settings.energy_cost} disabled={loading || busy} className="kd9-input w-full mt-2"
              onChange={e => setSettings(x=>({...x,energy_cost:e.target.value}))}/>
          </label>
          <label className="text-sm block">
            <span><Clock3 size={16} className="inline mr-1"/>Duración del acceso (horas)</span>
            <input type="number" data-testid="numerology-director-duration" min={5} max={8760} step={1} required
              value={settings.duration_hours} disabled={loading || busy} className="kd9-input w-full mt-2"
              onChange={e => setSettings(x=>({...x,duration_hours:e.target.value}))}/>
            <span className="block text-xs text-white/60 mt-1">168 horas = 7 días. Duración mínima 5 horas para permitir entregar los resultados después de cuatro horas.</span>
          </label>
        </div>
        <div className="text-sm text-amber-200/80">Oferta actual: {fmt(settings.energy_cost)} KD por {settings.duration_hours} horas. Los cambios afectan solo compras futuras, nunca modifican las fechas de accesos ya pagados.</div>
        <button className="kd9-btn-primary rounded-xl min-h-12 px-6 disabled:opacity-50" type="submit" disabled={busy || loading} data-testid="numerology-director-save">
          <Save size={16} className="inline mr-2"/>{busy ? "Guardando..." : "Guardar configuración"}
        </button>
      </form>
      <section className="kd9-card p-5 sm:p-7 space-y-4">
        <h2 className="font-cinzel text-xl kd9-gold-text">Resumen de compras</h2>
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
          {[
            ["Accesos vigentes", overview?.active_subscriptions],
            ["Accesos vencidos", overview?.expired_subscriptions],
            ["Compras registradas", overview?.total_purchases],
            ["Sinastrías generadas vigentes", overview?.sinastries_generated_active_or_pending_expiry],
            ["Estudios por entregar", overview?.numerology_studies_pending],
            ["Sinastrías por entregar", overview?.sinastries_pending],
            ["Energía cobrada", overview?.energy_charged],
          ].map(([label, value]) => <div key={label} className="rounded-xl border border-amber-400/20 p-3 bg-black/20"><div className="text-xs text-white/60">{label}</div><div className="font-cinzel text-xl mt-2">{fmt(value)}</div></div>)}
        </div>
      </section>
      <section className="kd9-card p-5 space-y-2">
        <h2 className="font-cinzel text-lg kd9-gold-text"><LockKeyhole size={18} className="inline mr-2"/>Control de saldos</h2>
        <p className="text-sm text-white/70">Para acreditar o retirar saldo a una billetera específica utiliza el módulo financiero oficial del Director. Conserva el movimiento atómico y el registro en el Ledger Maestro.</p>
        <Link to="/app/energia-kd-director" className="kd9-btn-outline inline-flex rounded-xl px-5 py-3 min-h-12">Abrir Gobierno de Energía KD9</Link>
      </section>
    </div>
  </PageShell>;
}
